import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiCopy, FiCheck, FiExternalLink } from 'react-icons/fi';
import backofficeService from '../../../services/backofficeService';
import './ScoreboardControl.css';

const DEFAULT_STATE = {
  home: 'POR',
  away: 'ESP',
  homeScore: 0,
  awayScore: 0,
  minute: 0,
  second: 0,
  running: false,
  period: 1,
  homeRedCards: 0,
  awayRedCards: 0,
  visible: true,
};

function pad(n) {
  return String(Math.max(0, n)).padStart(2, '0');
}

function formatTime(minute, second, period) {
  const totalSeconds = minute * 60 + second;
  if (period === 1 && totalSeconds > 45 * 60) {
    const added = totalSeconds - 45 * 60;
    return { main: '45:00', added: `+${pad(Math.floor(added / 60))}:${pad(added % 60)}` };
  }
  if (period === 2 && totalSeconds > 90 * 60) {
    const added = totalSeconds - 90 * 60;
    return { main: '90:00', added: `+${pad(Math.floor(added / 60))}:${pad(added % 60)}` };
  }
  return { main: `${pad(minute)}:${pad(second)}`, added: '' };
}

const ScoreboardControl = () => {
  const [state, setState] = useState(DEFAULT_STATE);
  const [homeInput, setHomeInput] = useState('');
  const [awayInput, setAwayInput] = useState('');
  const [minuteInput, setMinuteInput] = useState('0');
  const [secondInput, setSecondInput] = useState('0');
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);
  const initialised = useRef(false);

  const overlayUrl = `${window.location.origin}/scoreboard/overlay`;

  const fetchState = useCallback(async () => {
    try {
      const data = await backofficeService.getScoreboard();
      setState(data);
      // Only set text inputs once on first load — never overwrite while user is editing
      if (!initialised.current) {
        setHomeInput(data.home);
        setAwayInput(data.away);
        setMinuteInput(String(data.minute));
        setSecondInput(String(data.second));
        initialised.current = true;
      }
    } catch (err) {
      console.error('Scoreboard fetch error:', err);
    }
  }, []);

  useEffect(() => {
    fetchState();
    pollRef.current = setInterval(fetchState, 1000);
    return () => clearInterval(pollRef.current);
  }, [fetchState]);

  const send = useCallback(async (patch) => {
    try {
      const data = await backofficeService.updateScoreboard(patch);
      setState(data);
      return data;
    } catch (err) {
      console.error('Scoreboard update error:', err);
    }
  }, []);

  const updateTeams = async () => {
    const data = await send({ home: homeInput || 'POR', away: awayInput || 'ESP' });
    if (data) {
      setHomeInput(data.home);
      setAwayInput(data.away);
    }
  };

  const setTime = async () => {
    const m = parseInt(minuteInput, 10) || 0;
    const s = parseInt(secondInput, 10) || 0;
    const data = await send({ minute: m, second: s });
    if (data) {
      setMinuteInput(String(data.minute));
      setSecondInput(String(data.second));
    }
  };

  const handleCopyOverlay = async () => {
    await navigator.clipboard.writeText(overlayUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const time = formatTime(state.minute, state.second, state.period);

  return (
    <div className="backoffice-page scoreboard-control">
      <div className="page-header">
        <h1>⚽ Scoreboard</h1>
        <div className="overlay-link-group">
          <span className="overlay-url">{overlayUrl}</span>
          <button className="btn-secondary icon-btn" onClick={handleCopyOverlay} title="Copiar URL">
            {copied ? <FiCheck /> : <FiCopy />}
          </button>
          <a className="btn-secondary icon-btn" href={overlayUrl} target="_blank" rel="noreferrer" title="Abrir overlay">
            <FiExternalLink />
          </a>
        </div>
      </div>

      {/* Live preview */}
      <div className="sc-preview-wrap">
        <div className="sc-preview">
          <div className="sc-team-box">
            <div className="sc-red-cards">
              {Array.from({ length: state.homeRedCards }).map((_, i) => (
                <span key={i} className="sc-red-card" />
              ))}
            </div>
            <div className="sc-team">{state.home}</div>
          </div>
          <div className="sc-score">{state.homeScore} - {state.awayScore}</div>
          <div className="sc-team-box">
            <div className="sc-red-cards">
              {Array.from({ length: state.awayRedCards }).map((_, i) => (
                <span key={i} className="sc-red-card" />
              ))}
            </div>
            <div className="sc-team">{state.away}</div>
          </div>
          <div className="sc-timebox">
            <div className="sc-main-time">{time.main}</div>
            {time.added && <div className="sc-added-time">{time.added}</div>}
          </div>
        </div>
        {state.stoppageTime > 0 && (
          <div className="sc-stoppage-badge">+{state.stoppageTime}</div>
        )}
        <div className="sc-status">
          <span className={`status-badge ${state.running ? 'success' : 'warning'}`}>
            {state.running ? '▶ A correr' : '⏸ Pausado'}
          </span>
          <span className="status-badge info">Parte {state.period}</span>
        </div>
      </div>

      <div className="sc-grid">
        {/* Teams */}
        <div className="form-card sc-card">
          <h3>Equipas</h3>
          <div className="sc-row">
            <div className="form-group">
              <label>Casa</label>
              <input
                value={homeInput}
                onChange={e => setHomeInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && updateTeams()}
                maxLength={10}
                placeholder="POR"
              />
            </div>
            <div className="form-group">
              <label>Fora</label>
              <input
                value={awayInput}
                onChange={e => setAwayInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && updateTeams()}
                maxLength={10}
                placeholder="ESP"
              />
            </div>
          </div>
          <button className="btn-primary full-width" onClick={updateTeams}>Atualizar Equipas</button>
        </div>

        {/* Clock */}
        <div className="form-card sc-card">
          <h3>Cronómetro</h3>
          <div className="sc-clock-btns">
            <button
              className="btn-green large"
              onClick={() => send({ running: true })}
              disabled={state.running}
            >
              ▶ Start
            </button>
            <button
              className="btn-red-soft large"
              onClick={() => send({ running: false })}
              disabled={!state.running}
            >
              ⏸ Pause
            </button>
            <button
              className="btn-secondary large"
              onClick={() => send({ period: 1, minute: 0, second: 0, running: false })}
            >
              ↺ Reset Tempo
            </button>
          </div>
        </div>

        {/* Stoppage Time */}
        <div className="form-card sc-card">
          <h3>Minutos de Desconto</h3>
          <div className="sc-stoppage-display" style={{ marginBottom: 12 }}>
            {state.stoppageTime > 0 ? `+${state.stoppageTime}` : '—'}
          </div>
          <div className="form-group">
            <select
              value={state.stoppageTime || 0}
              onChange={e => send({ stoppageTime: parseInt(e.target.value, 10) })}
            >
              <option value={0}>Sem desconto</option>
              {Array.from({ length: 15 }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>+{n} min</option>
              ))}
            </select>
          </div>
        </div>

        {/* Score */}
        <div className="form-card sc-card">
          <h3>Resultado</h3>
          <div className="sc-btn-grid">
            <button className="btn-green" onClick={() => send({ homeScore: state.homeScore + 1 })}>Casa +1</button>
            <button className="btn-red-soft" onClick={() => send({ homeScore: Math.max(0, state.homeScore - 1) })}>Casa -1</button>
            <button className="btn-green" onClick={() => send({ awayScore: state.awayScore + 1 })}>Fora +1</button>
            <button className="btn-red-soft" onClick={() => send({ awayScore: Math.max(0, state.awayScore - 1) })}>Fora -1</button>
          </div>
          <button className="btn-secondary full-width" onClick={() => send({ homeScore: 0, awayScore: 0 })}>
            Reset Resultado
          </button>
        </div>

        {/* Red Cards */}
        <div className="form-card sc-card">
          <h3>Cartões Vermelhos</h3>
          <div className="sc-btn-grid">
            <button className="btn-red-card" onClick={() => send({ homeRedCards: Math.min(4, state.homeRedCards + 1) })}>🟥 Casa +1</button>
            <button className="btn-red-soft" onClick={() => send({ homeRedCards: Math.max(0, state.homeRedCards - 1) })}>🟥 Casa -1</button>
            <button className="btn-red-card" onClick={() => send({ awayRedCards: Math.min(4, state.awayRedCards + 1) })}>🟥 Fora +1</button>
            <button className="btn-red-soft" onClick={() => send({ awayRedCards: Math.max(0, state.awayRedCards - 1) })}>🟥 Fora -1</button>
          </div>
          <button className="btn-secondary full-width" onClick={() => send({ homeRedCards: 0, awayRedCards: 0 })}>
            Reset Cartões
          </button>
        </div>

        {/* Period */}
        <div className="form-card sc-card">
          <h3>Parte do Jogo</h3>
          <div className="sc-btn-grid-3">
            <button
              className={state.period === 1 && state.minute === 0 && state.second === 0 ? 'btn-primary' : 'btn-secondary'}
              onClick={() => send({ period: 1, minute: 0, second: 0, running: false })}
            >
              1ª Parte — 00:00
            </button>
            <button
              className={state.period === 2 && state.minute === 45 && state.second === 0 ? 'btn-primary' : 'btn-secondary'}
              onClick={() => send({ period: 2, minute: 45, second: 0, running: false })}
            >
              2ª Parte — 45:00
            </button>
            <button
              className="btn-secondary"
              onClick={() => send({ period: 2, minute: 90, second: 0, running: false })}
            >
              90:00 — FT
            </button>
          </div>
        </div>

        {/* Manual Time */}
        <div className="form-card sc-card">
          <h3>Tempo Manual</h3>
          <div className="sc-row">
            <div className="form-group">
              <label>Minuto</label>
              <input
                type="number"
                min="0"
                value={minuteInput}
                onChange={e => setMinuteInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && setTime()}
              />
            </div>
            <div className="form-group">
              <label>Segundo</label>
              <input
                type="number"
                min="0"
                max="59"
                value={secondInput}
                onChange={e => setSecondInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && setTime()}
              />
            </div>
          </div>
          <button className="btn-primary full-width" onClick={setTime}>Definir Tempo</button>
          <div className="sc-time-nudge">
            <button className="btn-secondary" onClick={() => send({ minute: state.minute + 1, second: state.second })}>+1 Min</button>
            <button className="btn-secondary" onClick={() => send({ minute: Math.max(0, state.minute - 1), second: state.second })}>-1 Min</button>
            <button className="btn-secondary" onClick={() => {
              let s = state.second + 10;
              let m = state.minute + Math.floor(s / 60);
              send({ minute: m, second: s % 60 });
            }}>+10 Seg</button>
            <button className="btn-secondary" onClick={() => {
              const total = Math.max(0, state.minute * 60 + state.second - 10);
              send({ minute: Math.floor(total / 60), second: total % 60 });
            }}>-10 Seg</button>
            <button className="btn-secondary" onClick={() => {
              const total = state.minute * 60 + state.second + 1;
              send({ minute: Math.floor(total / 60), second: total % 60 });
            }}>+1 Seg</button>
            <button className="btn-secondary" onClick={() => {
              const total = Math.max(0, state.minute * 60 + state.second - 1);
              send({ minute: Math.floor(total / 60), second: total % 60 });
            }}>-1 Seg</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoreboardControl;
